import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  inject,
} from '@angular/core';
import { SessionStorageService } from '../../services/session-storage.service';
import { RightBridgeApiService } from '../../services/right-bridge-api.service';
import { ReportParams } from '../../models/unified-print-model';
import { Platform } from '@angular/cdk/platform';
import { EnvironmentService } from '../../services/environment.service';
import { saveAs } from '@progress/kendo-file-saver';
import cloneDeep from 'lodash-es/cloneDeep';
import { FormsModule } from '@angular/forms';
import { NgxLoadingModule } from 'ngx-loading';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { GroupProfile } from '../../models/global.model';
import { HasPermissionDirective } from '../../directives/permissions.directive';

@Component({
  selector: 'app-unified-print',
  templateUrl: './unified-print.component.html',
  styleUrls: ['./unified-print.component.scss'],
  imports: [
    FormsModule,
    NgxLoadingModule,
    MatButtonModule,
    MatInputModule,
    MatIconModule,
    MatCheckboxModule,
    HasPermissionDirective,
  ],
})
export class UnifiedPrintComponent implements OnInit {
  private rbs = inject(RightBridgeApiService);
  private ss = inject(SessionStorageService);
  private pltfrm = inject(Platform);
  private envSvc = inject(EnvironmentService);

  @Input() profile;
  @Input() silentPrint;
  @Input() appStatus;
  @Output() closeDialog = new EventEmitter();

  customFilename;
  printParams!: GroupProfile;
  appAccess = [];
  enablePrint = {
    AWReport: false,
    AWClientReport: false,
    AnnuityWizardExchange: false,
    ProductProfiler: false,
    InvestmentWizard: false,
    LifeWizard: false,
    FeeCompare: false,
    Rollover: false,
  };
  loading = false;
  awProductsSelected = false;
  reportPayload: ReportParams = {} as ReportParams;
  rights: string[];
  firm;
  environment;
  baseUrl;
  generating = false;
  enableFeeCompare = false;
  showFeeCompare = false;

  ngOnInit(): void {
    this.environment = this.envSvc.get();
    this.baseUrl = this.environment.apiBase;
    this.loading = true;
    this.appAccess = this.ss.get('globals')?.Modules;
    this.rights = this.ss.get('rights');
    this.firm = this.ss.get('firm');

    this.reportPayload = {
      profileID: null,
      FeeCompare: {
        includeIW: false,
        includeAW: false,
        includeLI: false,
      },
      ProductProfiler: {
        includeDetail: false,
        returnReport: false,
        IncludeFeeCompare: false,
        UseRolloverReport: false,
      },
      InvestmentWizard: {
        includeDetail: false,
        firmID: null,
        includeAlternatives: false,
        includeFundAlternativesChart: false,
      },
      AWReport: {
        bridgeIds: [],
        ivao: false,
        isValidation: false,
        excludeAlternatives: false,
      },
      AWClientReport: {
        bridgeIds: [],
        isValidation: true,
        excludeAlternatives: false,
        includeVAInvestmentOptions: false,
        returnReport: false,
      },
      AnnuityWizardExchange: {
        returnReport: false,
      },
      LifeWizard: {
        bridgeIds: [],
      },
    };

    if (this.appStatus) {
      this.enablePrint.ProductProfiler = this.appStatus.pp;
      this.enablePrint.InvestmentWizard = this.appStatus.iw;
      this.enablePrint.AWClientReport =
        this.appStatus.aw && this.rights.includes('AwCFVReport');
      this.enablePrint.AWReport =
        this.appStatus.aw && this.rights.includes('AwFullReport');
      this.enablePrint.LifeWizard = this.appStatus.li;
    }

    this.getPrintData();
  }

  getPrintData() {
    this.loading = true;
    this.rbs
      .getUnifiedPrintParameters(this.profile)
      .subscribe((res: GroupProfile) => {
        this.printParams = res;

        if (
          Object.keys(this.printParams.AWSelectedProduct).length > 0 &&
          this.printParams.AWSelectedProduct.CarrierName.length > 0
        ) {
          this.awProductsSelected = true;
          this.reportPayload.FeeCompare.includeAW = true;
        }
        if (this.printParams.UnitPrintDefaults) {
          if (
            this.printParams.UnitPrintDefaults.ConsolidatedSignaturePageInclude
          ) {
            this.reportPayload['SignaturePage'] = {};
            this.reportPayload['SignaturePage']['include'] =
              this.printParams.UnitPrintDefaults.ConsolidatedSignaturePageFile;
          }

          if (
            this.printParams.PrintOutput.EnableFeeCompare &&
            (this.printParams.UnitPrintDefaults.PPIncludeFeeCompare === 1 ||
              this.printParams.UnitPrintDefaults.PPIncludeFeeCompare === 3) &&
            this.printParams.ClientProfilingOut.ValidPPRan &&
            this.printParams.ClientProfilingOut.ValidPPRan.toLowerCase() === 'y'
          ) {
            this.enablePrint.FeeCompare = true;
          }

          if (this.printParams.UnitPrintDefaults.AWAlternatives) {
            this.reportPayload.AWReport.excludeAlternatives = true;
            this.reportPayload.AWClientReport.excludeAlternatives = true;
          }

          if (
            this.printParams.UnitPrintDefaults.IWFundDetails === 1 ||
            this.printParams.UnitPrintDefaults.IWFundDetails === 3
          ) {
            this.reportPayload.InvestmentWizard.includeDetail = true;
          }

          if (
            this.printParams.UnitPrintDefaults.IWProgramDetails === 1 ||
            this.printParams.UnitPrintDefaults.IWProgramDetails === 3
          ) {
            this.reportPayload.InvestmentWizard.includeAlternatives = true;
          }

          if (
            this.printParams.UnitPrintDefaults.IWFundAlternatives === 1 ||
            this.printParams.UnitPrintDefaults.IWFundAlternatives === 3
          ) {
            this.reportPayload.InvestmentWizard.includeFundAlternativesChart = true;
          }
        } else {
          this.printParams.UnitPrintDefaults = {};
        }

        if (
          this.printParams.ClientProfilingOut.ValidPPRan &&
          this.printParams.ClientProfilingOut.ValidPPRan.toLowerCase() === 'y'
        ) {
          this.enableFeeCompare = true;
        }

        this.showFeeCompare = this.printParams.PrintOutput.EnableFeeCompare;

        if (this.printParams.InvestmentWizard.SelectedProgram) {
          this.reportPayload.FeeCompare.includeIW = true;
        }

        if (this.printParams.LISelectedProduct.LifeItemID) {
          this.reportPayload.FeeCompare.includeLI = true;
        }

        if (this.printParams.UnitPrintDefaults.HideConsolidatedPrintModal) {
          this.generateReport();
        }

        this.loading = false;
      });
  }

  updateApps(ev, app) {
    if (app !== 'FeeCompare') {
      this.enablePrint[app] = !this.enablePrint[app];
    } else if (app === 'FeeCompare') {
      let enabled = false;

      Object.keys(this.reportPayload.FeeCompare).forEach(x => {
        if (this.reportPayload.FeeCompare[x]) {
          enabled = true;
        }
      });

      if (enabled) {
        this.enablePrint[app] = true;
      }
    }

    if (
      (app === 'ProductProfiler' || app === 'FeeCompare') &&
      this.enablePrint.FeeCompare
    ) {
      this.reportPayload.ProductProfiler.IncludeFeeCompare = true;
    }
  }

  updateReportParams(ev, section, field) {
    this.reportPayload[section][field] = !this.reportPayload[section][field];
  }

  cancel() {
    this.closeDialog.emit();
  }

  generateReport() {
    this.loading = true;

    this.reportPayload.profileID = this.profile;

    const payload = cloneDeep(this.reportPayload);

    Object.keys(this.enablePrint).forEach(x => {
      if (!this.enablePrint[x]) {
        delete payload[x];
      }
    });

    if (payload.AWReport) {
      if (Object.keys(this.printParams.AWSelectedProduct).length > 0) {
        payload.AWReport.bridgeIds = [
          this.printParams.AWSelectedProduct.BridgeID,
        ];
      }
    }

    if (payload.AWClientReport) {
      if (Object.keys(this.printParams.AWSelectedProduct).length > 0) {
        payload.AWClientReport.bridgeIds = [
          this.printParams.AWSelectedProduct.BridgeID,
        ];
      }
    }

    if (payload.InvestmentWizard) {
      const firmID = this.firm ? this.firm : this.ss.get('globals').firmId;
      payload.InvestmentWizard.firmID = firmID;
    }

    if (payload.LifeWizard) {
      payload.LifeWizard.bridgeIds =
        this.printParams.LISelectedProduct.LifeItemID;
    }

    if (this.silentPrint) {
      this.closeDialog.emit(payload);
    } else {
      this.generating = true;
      const date = new Date();

      this.rbs.getUnifiedReport(payload, 'rb').subscribe(data => {
        const filename = this.customFilename
          ? this.customFilename
          : 'RightBridge-UnifiedReport-' +
            (date.getMonth() + 1) +
            date.getDate() +
            date.getFullYear() +
            '.pdf';
        const iosSafari =
          this.pltfrm.IOS ||
          (navigator?.['userAgentData']?.platform === 'macOS' &&
            navigator.maxTouchPoints > 1);
        let fileData;
        const reader = new FileReader();

        reader.readAsDataURL(data);
        reader.onloadend = () => {
          fileData = reader.result;
          saveAs(fileData, filename, {
            proxyURL: `${this.baseUrl}/util/proxy`,
            proxyTarget: '_self',
            forceProxy: iosSafari,
          });
          this.closeDialog.emit('report generated');
          this.generating = false;
          this.loading = false;
        };
      });
    }
  }
}
